import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { derivePrimaryTokens, DEFAULT_PRIMARY_HEX } from '@/lib/theme';

/**
 * Tema durumu.
 *
 * ÖNEMLİ: Bu store'un localStorage anahtarı ('mallos.theme') ve alan adları,
 * index.html içindeki FOUC-engelleyici betikle BİREBİR aynı olmak zorundadır.
 * O betik React'ten önce çalışıp aynı JSON'u okur. Buradaki bir alan adını
 * değiştirirseniz index.html'i de güncelleyin, aksi halde sayfa yenilendiğinde
 * bir anlık "beyaz flaş" geri gelir.
 */

export type ColorMode = 'light' | 'dark' | 'system';
export type Direction = 'ltr' | 'rtl';
export type NavLayout = 'vertical' | 'horizontal';
export type SidebarState = 'expanded' | 'icon' | 'hidden';
export type SurfaceTone = 'light' | 'dark';
export type ContainerWidth = 'fluid' | 'boxed';
export type Density = 'comfortable' | 'compact';

export interface ThemeState {
  colorMode: ColorMode;
  direction: Direction;
  navLayout: NavLayout;
  sidebarState: SidebarState;
  menuTone: SurfaceTone;
  headerTone: SurfaceTone;
  containerWidth: ContainerWidth;
  density: Density;

  /** Kullanıcının seçtiği ana renk (HEX). */
  primaryHex: string;
  /** Türetilmiş CSS değerleri — index.html betiği de bunları okur. */
  primaryRgb: string;
  primaryStrongRgb: string;

  /** Mobilde sol menünün açık olup olmadığı (kalıcı DEĞİL, oturumluk). */
  mobileNavOpen: boolean;
}

export interface ThemeActions {
  setPartial: (patch: Partial<ThemeState>) => void;
  setPrimary: (hex: string) => void;
  toggleColorMode: () => void;
  toggleSidebar: () => void;
  setMobileNavOpen: (open: boolean) => void;
  reset: () => void;
}

const initialTokens = derivePrimaryTokens(DEFAULT_PRIMARY_HEX) ?? {
  primaryRgb: '143, 189, 86',
  primaryStrongRgb: '85, 125, 40',
};

const DEFAULTS: ThemeState = {
  colorMode: 'light',
  direction: 'ltr',
  navLayout: 'vertical',
  sidebarState: 'expanded',
  menuTone: 'light',
  headerTone: 'light',
  containerWidth: 'fluid',
  density: 'comfortable',
  primaryHex: DEFAULT_PRIMARY_HEX,
  primaryRgb: initialTokens.primaryRgb,
  primaryStrongRgb: initialTokens.primaryStrongRgb,
  mobileNavOpen: false,
};

export const useThemeStore = create<ThemeState & ThemeActions>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setPartial: (patch) => set(patch),

      /**
       * Ana rengi değiştirir ve AA-güvenli "güçlü" varyantı otomatik türetir.
       *
       * Bu türetme olmadan kullanıcı açık bir pastel seçtiğinde linkler ve
       * odak halkaları okunmaz hale gelirdi. Kısıtı store'a koymak, her
       * çağrı yerinde hatırlamaktan güvenli.
       */
      setPrimary: (hex) => {
        const isDark = resolveColorMode(get().colorMode) === 'dark';
        const tokens = derivePrimaryTokens(hex, isDark);
        if (!tokens) return; // geçersiz HEX — sessizce yok say, arayüzü bozma
        set({ primaryHex: hex, ...tokens });
      },

      toggleColorMode: () => {
        const next: ColorMode = resolveColorMode(get().colorMode) === 'dark' ? 'light' : 'dark';
        // Renk modu değişince güçlü varyant yeniden hesaplanmalı:
        // koyu zeminde AA-güvenli renk AÇIK yöndedir, açık zeminde KOYU.
        const tokens = derivePrimaryTokens(get().primaryHex, next === 'dark');
        set({ colorMode: next, ...(tokens ?? {}) });
      },

      toggleSidebar: () => {
        const current = get().sidebarState;
        set({ sidebarState: current === 'expanded' ? 'icon' : 'expanded' });
      },

      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'mallos.theme',
      // mobileNavOpen kalıcı olmamalı: kullanıcı menü açıkken sayfayı
      // yenilediğinde menünün açık gelmesi beklenmedik bir davranış olur.
      partialize: ({ mobileNavOpen: _omit, ...rest }) => rest,
    },
  ),
);

/** 'system' seçeneğini işletim sistemi tercihine göre çözer. */
export function resolveColorMode(mode: ColorMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
